import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { dashboardAPI, tasksAPI, inventoryAPI } from '../lib/api';
import { formatDate, getDaysUntil, getStatusColor, getStatusLabel } from '../lib/utils';
import PageSkeleton from '../components/PageSkeleton';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import TranslatableText from '../components/TranslatableText';
import { 
  LayoutDashboard, 
  ClipboardList, 
  Plus, 
  AlertTriangle,
  Clock,
  CheckCircle2,
  FileWarning,
  ArrowRight,
  Calendar,
  Loader2,
  Users,
  Package,
  UserCheck,
  Building2,
  Settings,
  DollarSign,
  TrendingUp,
  Crown,
  CreditCard,
  BarChart3,
  Database
} from 'lucide-react';
import Layout from '../components/Layout';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';
  const isSuperAdmin = user?.role === 'super_admin';

  // Live clock update
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // For super admin, only fetch stats (no tasks)
        if (isSuperAdmin) {
          const statsRes = await dashboardAPI.getStats();
          setStats(statsRes.data);
        } else {
          const [statsRes, tasksRes] = await Promise.all([
            dashboardAPI.getStats(),
            tasksAPI.getAll()
          ]);
          setStats(statsRes.data);
          setRecentTasks(tasksRes.data.slice(0, 5));
        }
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [isSuperAdmin]);

  if (loading) {
    return <PageSkeleton title="Loading dashboard" />;
  }

  // Super Admin Dashboard - Shows tenant/company data
  if (isSuperAdmin && stats?.is_super_admin) {
    return (
      <Layout>
        <div className="space-y-8" data-testid="dashboard-page">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white">
                Admin Dashboard
              </h1>
              <p className="text-slate-400 mt-1">
                Welcome back, {user?.name} 
                <span className="ml-2 px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded-sm uppercase">
                  <Crown className="w-3 h-3 inline mr-1" />
                  {user?.role?.replace('_', ' ')}
                </span>
              </p>
            </div>
            
            {/* Live Clock & Super Admin Button */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
              <div className="card-industrial flex items-center justify-center gap-2 px-3 py-2 sm:px-4 sm:py-3">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400 flex-shrink-0" />
                <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2 sm:gap-0">
                  <p className="text-white font-mono text-sm sm:text-lg font-bold whitespace-nowrap">
                    {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </p>
                  <p className="text-slate-400 text-xs whitespace-nowrap">
                    {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </p>
                </div>
              </div>
              
              <Button 
                onClick={() => navigate('/super-admin')} 
                className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                <Building2 className="w-5 h-5" />
                Manage Companies
              </Button>
            </div>
          </div>

          {/* Revenue Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div 
              onClick={() => navigate('/super-admin/revenue')}
              className="card-industrial clickable p-4 group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider">Monthly Revenue</p>
                  <p className="text-3xl font-bold text-green-400 mt-1 group-hover:text-green-300 transition-colors">${stats.financials?.mrr?.toLocaleString()}</p>
                  <p className="text-slate-500 text-sm">MRR</p>
                </div>
                <div className="w-12 h-12 rounded-sm bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                  <DollarSign className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </div>
            
            <div 
              onClick={() => navigate('/super-admin/revenue')}
              className="card-industrial clickable p-4 group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider">Annual Revenue</p>
                  <p className="text-3xl font-bold text-green-400 mt-1 group-hover:text-green-300 transition-colors">${stats.financials?.arr?.toLocaleString()}</p>
                  <p className="text-slate-500 text-sm">ARR</p>
                </div>
                <div className="w-12 h-12 rounded-sm bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                  <TrendingUp className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </div>
            
            <div 
              onClick={() => navigate('/super-admin')}
              className="card-industrial clickable p-4 group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider">Total Companies</p>
                  <p className="text-3xl font-bold text-amber-400 mt-1 group-hover:text-amber-300 transition-colors">{stats.companies?.total || 0}</p>
                  <p className="text-slate-500 text-sm">{stats.companies?.paying || 0} paying</p>
                </div>
                <div className="w-12 h-12 rounded-sm bg-amber-500/10 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                  <Building2 className="w-6 h-6 text-amber-500" />
                </div>
              </div>
            </div>
            
            <div 
              onClick={() => navigate('/super-admin/users')}
              className="card-industrial clickable p-4 group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider">Total Users</p>
                  <p className="text-3xl font-bold text-cyan-400 mt-1 group-hover:text-cyan-300 transition-colors">{stats.users?.total || 0}</p>
                  <p className="text-slate-500 text-sm">{stats.users?.admins || 0} admins</p>
                </div>
                <div className="w-12 h-12 rounded-sm bg-cyan-500/10 flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
                  <Users className="w-6 h-6 text-cyan-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Plan Distribution & Recent Companies */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Plan Distribution */}
            <div className="card-industrial p-6">
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-amber-500" />
                Subscription Plans
              </h2>
              <div className="space-y-3">
                <div 
                  onClick={() => navigate('/super-admin/subscriptions')}
                  className="flex items-center justify-between p-3 bg-slate-900 rounded cursor-pointer hover:bg-slate-800 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    <span className="text-white">Trial</span>
                  </div>
                  <span className="text-amber-400 font-bold">{stats.plans?.trial || 0}</span>
                </div>
                <div 
                  onClick={() => navigate('/super-admin/subscriptions')}
                  className="flex items-center justify-between p-3 bg-slate-900 rounded cursor-pointer hover:bg-slate-800 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-white">Basic</span>
                  </div>
                  <span className="text-blue-400 font-bold">{stats.plans?.basic || 0}</span>
                </div>
                <div 
                  onClick={() => navigate('/super-admin/subscriptions')}
                  className="flex items-center justify-between p-3 bg-slate-900 rounded cursor-pointer hover:bg-slate-800 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                    <span className="text-white">Pro ($29/mo)</span>
                  </div>
                  <span className="text-purple-400 font-bold">{stats.plans?.pro || 0}</span>
                </div>
                <div 
                  onClick={() => navigate('/super-admin/subscriptions')}
                  className="flex items-center justify-between p-3 bg-slate-900 rounded cursor-pointer hover:bg-slate-800 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-white">Enterprise (Contact us)</span>
                  </div>
                  <span className="text-green-400 font-bold">{stats.plans?.enterprise || 0}</span>
                </div>
                <div 
                  onClick={() => navigate('/super-admin/subscriptions')}
                  className="flex items-center justify-between p-3 bg-slate-900 rounded cursor-pointer hover:bg-slate-800 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-slate-500"></div>
                    <span className="text-white">Free (Comped)</span>
                  </div>
                  <span className="text-slate-400 font-bold">{stats.plans?.free || 0}</span>
                </div>
              </div>
            </div>

            {/* Recent Companies */}
            <div className="card-industrial p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-amber-500" />
                  Recent Companies
                </h2>
                <Link to="/super-admin" className="text-cyan-400 text-sm hover:text-cyan-300 flex items-center gap-1">
                  View All <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              
              {stats.recent_companies?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_companies.map((company) => (
                    <div 
                      key={company.id} 
                      onClick={() => navigate('/super-admin')}
                      className="p-3 bg-slate-900 rounded flex items-center justify-between cursor-pointer hover:bg-slate-800 transition-colors"
                    >
                      <div>
                        <p className="text-white font-medium">{company.name}</p>
                        <p className="text-slate-500 text-sm">/{company.slug}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs rounded uppercase ${
                          company.plan === 'enterprise' ? 'bg-green-500/20 text-green-400' :
                          company.plan === 'pro' ? 'bg-purple-500/20 text-purple-400' :
                          company.plan === 'basic' ? 'bg-blue-500/20 text-blue-400' :
                          company.plan === 'trial' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          {company.plan}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No companies yet</p>
                  <Button 
                    onClick={() => navigate('/super-admin')} 
                    variant="outline"
                    className="mt-4 btn-secondary"
                  >
                    Create First Company
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Platform Overview */}
          <div className="card-industrial p-6">
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-amber-500" />
              Platform Overview
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div 
                onClick={() => navigate('/super-admin/analytics')}
                className="bg-slate-900 p-4 rounded text-center cursor-pointer hover:bg-slate-800 transition-colors"
              >
                <p className="text-slate-400 text-xs uppercase">Total Work Orders</p>
                <p className="text-2xl font-bold text-white mt-1">{stats.total_work_orders || 0}</p>
                <p className="text-slate-500 text-xs">across all companies</p>
              </div>
              <div 
                onClick={() => navigate('/super-admin')}
                className="bg-slate-900 p-4 rounded text-center cursor-pointer hover:bg-slate-800 transition-colors"
              >
                <p className="text-slate-400 text-xs uppercase">Active Companies</p>
                <p className="text-2xl font-bold text-green-400 mt-1">{stats.companies?.active || 0}</p>
                <p className="text-slate-500 text-xs">not suspended</p>
              </div>
              <div 
                onClick={() => navigate('/super-admin/subscriptions')}
                className="bg-slate-900 p-4 rounded text-center cursor-pointer hover:bg-slate-800 transition-colors"
              >
                <p className="text-slate-400 text-xs uppercase">Trial Companies</p>
                <p className="text-2xl font-bold text-amber-400 mt-1">{stats.companies?.trial || 0}</p>
                <p className="text-slate-500 text-xs">in trial period</p>
              </div>
              <div 
                onClick={() => navigate('/super-admin/subscriptions')}
                className="bg-slate-900 p-4 rounded text-center cursor-pointer hover:bg-slate-800 transition-colors"
              >
                <p className="text-slate-400 text-xs uppercase">Paying Customers</p>
                <p className="text-2xl font-bold text-cyan-400 mt-1">{stats.companies?.paying || 0}</p>
                <p className="text-slate-500 text-xs">active subscriptions</p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button 
              onClick={() => navigate('/super-admin')} 
              className="btn-secondary h-auto py-4 flex flex-col items-center gap-2"
            >
              <Building2 className="w-6 h-6" />
              <span>Manage Companies</span>
            </Button>
            <Button 
              onClick={() => navigate('/super-admin?tab=billing')} 
              className="btn-secondary h-auto py-4 flex flex-col items-center gap-2"
            >
              <CreditCard className="w-6 h-6" />
              <span>Billing Overview</span>
            </Button>
            <Button 
              onClick={() => navigate('/super-admin/backup')} 
              className="btn-secondary h-auto py-4 flex flex-col items-center gap-2"
            >
              <Database className="w-6 h-6" />
              <span>Backup & Restore</span>
            </Button>
            <Button 
              onClick={() => navigate('/super-admin?tab=cleanup')} 
              className="btn-secondary h-auto py-4 flex flex-col items-center gap-2"
            >
              <Settings className="w-6 h-6" />
              <span>Data Cleanup</span>
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // Regular user/admin dashboard
  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white">
              {t('dashboard.title')}
            </h1>
            <p className="text-slate-400 mt-1">
              {t('dashboard.welcomeBack')}, {user?.name} 
              <span className="ml-2 px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded-sm uppercase">
                {user?.role}
              </span>
            </p>
          </div>
          
          {/* Live Clock & New Work Order Button */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
            {/* Live Clock - Compact on mobile */}
            <div className="card-industrial flex items-center justify-center gap-2 px-3 py-2 sm:px-4 sm:py-3" data-testid="live-clock">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400 flex-shrink-0" />
              <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2 sm:gap-0">
                <p className="text-white font-mono text-sm sm:text-lg font-bold whitespace-nowrap">
                  {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </p>
                <p className="text-slate-400 text-xs whitespace-nowrap">
                  {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </p>
              </div>
            </div>
            
            <Button 
              onClick={() => navigate('/tasks/new')} 
              className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
              data-testid="create-task-btn"
            >
              <Plus className="w-5 h-5" />
              {t('common.newWorkOrder')}
            </Button>
          </div>
        </div>

        {/* Admin Quick Links */}
        {isAdmin && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Link 
              to="/admin/users" 
              className="card-industrial flex items-center gap-3 hover:border-amber-500/50 transition-colors"
              data-testid="admin-users-link"
            >
              <div className="w-10 h-10 bg-blue-500/20 rounded-sm flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-medium">{stats?.user_count || 0} {t('common.users')}</p>
                <p className="text-slate-400 text-xs uppercase">{t('dashboard.manageTeam')}</p>
              </div>
            </Link>
            
            <Link 
              to="/admin/locations" 
              className="card-industrial flex items-center gap-3 hover:border-amber-500/50 transition-colors"
              data-testid="admin-locations-link"
            >
              <div className="w-10 h-10 bg-green-500/20 rounded-sm flex items-center justify-center">
                <Building2 className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-white font-medium">{t('common.locations')}</p>
                <p className="text-slate-400 text-xs uppercase">{t('dashboard.buildingsFloors')}</p>
              </div>
            </Link>
            
            <Link 
              to="/admin/categories" 
              className="card-industrial flex items-center gap-3 hover:border-amber-500/50 transition-colors"
              data-testid="admin-categories-link"
            >
              <div className="w-10 h-10 bg-purple-500/20 rounded-sm flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-white font-medium">{t('common.categories')}</p>
                <p className="text-slate-400 text-xs uppercase">{t('dashboard.workOrderTypes')}</p>
              </div>
            </Link>
            
            <Link 
              to="/admin/inventory" 
              className="card-industrial flex items-center gap-3 hover:border-amber-500/50 transition-colors"
              data-testid="admin-inventory-link"
            >
              <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${stats?.low_stock_count > 0 ? 'bg-red-500/20' : 'bg-yellow-500/20'}`}>
                <Package className={`w-5 h-5 ${stats?.low_stock_count > 0 ? 'text-red-400' : 'text-yellow-400'}`} />
              </div>
              <div>
                <p className="text-white font-medium">{t('common.inventory')}</p>
                <p className={`text-xs uppercase ${stats?.low_stock_count > 0 ? 'text-red-400' : 'text-slate-400'}`}>
                  {stats?.low_stock_count > 0 ? `${stats.low_stock_count} ${t('dashboard.lowStock')}` : t('dashboard.stockLevels')}
                </p>
              </div>
            </Link>
            
            <Link 
              to="/admin/settings" 
              className="card-industrial flex items-center gap-3 hover:border-amber-500/50 transition-colors"
              data-testid="admin-settings-link"
            >
              <div className="w-10 h-10 bg-slate-600/50 rounded-sm flex items-center justify-center">
                <Settings className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-white font-medium">{t('common.settings')}</p>
                <p className="text-slate-400 text-xs uppercase">{t('dashboard.brandingTheme')}</p>
              </div>
            </Link>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {/* Total Work Orders */}
          <div 
            className="card-industrial clickable stats-card group" 
            data-testid="stat-total-tasks"
            onClick={() => navigate('/tasks')}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 uppercase text-xs tracking-wider font-mono">{t('dashboard.totalWorkOrders')}</p>
                <p className="text-3xl md:text-4xl font-bold text-white mt-1 group-hover:text-amber-300 transition-colors">{stats?.total_tasks || 0}</p>
              </div>
              <div className="w-10 h-10 bg-slate-700 rounded-sm flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                <ClipboardList className="w-5 h-5 text-slate-400 group-hover:text-amber-400 transition-colors" />
              </div>
            </div>
          </div>

          {/* Reported/Unassigned */}
          <div 
            className="card-industrial clickable stats-card group" 
            data-testid="stat-reported"
            onClick={() => navigate('/tasks?status=reported')}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 uppercase text-xs tracking-wider font-mono">{t('status.reported')}</p>
                <p className="text-3xl md:text-4xl font-bold text-blue-400 mt-1 group-hover:text-blue-300 transition-colors">{stats?.by_status?.reported || 0}</p>
              </div>
              <div className="w-10 h-10 bg-blue-500/20 rounded-sm flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                <FileWarning className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </div>

          {/* Assigned */}
          <div 
            className="card-industrial clickable stats-card group" 
            data-testid="stat-assigned"
            onClick={() => navigate('/tasks?status=assigned')}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 uppercase text-xs tracking-wider font-mono">{t('status.assigned')}</p>
                <p className="text-3xl md:text-4xl font-bold text-violet-300 mt-1">{stats?.by_status?.assigned || 0}</p>
              </div>
              <div className="w-10 h-10 bg-violet-500/20 rounded-sm flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-violet-300" />
              </div>
            </div>
          </div>

          {/* In Progress */}
          <div 
            className="card-industrial clickable stats-card group" 
            data-testid="stat-in-progress"
            onClick={() => navigate('/tasks?status=in_progress')}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 uppercase text-xs tracking-wider font-mono">{t('dashboard.inProgress')}</p>
                <p className="text-3xl md:text-4xl font-bold text-yellow-400 mt-1 group-hover:text-yellow-300 transition-colors">
                  {stats?.by_status?.in_progress || 0}
                </p>
              </div>
              <div className="w-10 h-10 bg-yellow-500/20 rounded-sm flex items-center justify-center group-hover:bg-yellow-500/30 transition-colors">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
            </div>
          </div>

          {/* Completed */}
          <div 
            className="card-industrial clickable stats-card group" 
            data-testid="stat-completed"
            onClick={() => navigate('/tasks?status=completed')}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 uppercase text-xs tracking-wider font-mono">{t('dashboard.completed')}</p>
                <p className="text-3xl md:text-4xl font-bold text-green-400 mt-1 group-hover:text-green-300 transition-colors">{stats?.by_status?.completed || 0}</p>
              </div>
              <div className="w-10 h-10 bg-green-500/20 rounded-sm flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Urgent Section */}
        {(stats?.urgent?.critical > 0 || stats?.urgent?.high > 0) && (
          <div className="card-industrial border-red-500/50 bg-red-500/5" data-testid="urgent-section">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-red-400">
                {t('dashboard.urgentAttention')}
              </h2>
            </div>
            <div className="flex gap-6">
              {stats?.urgent?.critical > 0 && (
                <Link to="/tasks?priority=critical" className="flex items-center gap-2 hover:text-red-300 transition-colors">
                  <span className="priority-indicator critical"></span>
                  <span className="text-white font-medium">{stats.urgent.critical} {t('dashboard.criticalPriority')}</span>
                </Link>
              )}
              {stats?.urgent?.high > 0 && (
                <Link to="/tasks?priority=high" className="flex items-center gap-2 hover:text-orange-300 transition-colors">
                  <span className="priority-indicator high"></span>
                  <span className="text-white font-medium">{stats.urgent.high} {t('dashboard.highPriority')}</span>
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Two Column Layout */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Upcoming Deadlines */}
          <div className="card-industrial" data-testid="upcoming-deadlines">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-amber-500" />
                {t('dashboard.upcomingDeadlines')}
              </h2>
            </div>
            
            {stats?.upcoming_deadlines?.length > 0 ? (
              <div className="space-y-3">
                {stats.upcoming_deadlines.map((task) => {
                  const daysUntil = getDaysUntil(task.deadline);
                  const isOverdue = daysUntil < 0;
                  
                  return (
                    <Link
                      key={task.id}
                      to={`/tasks/${task.id}`}
                      className="block p-3 bg-slate-700/50 border border-slate-600 rounded-sm hover:border-amber-500/50 transition-colors"
                      data-testid={`deadline-task-${task.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">
                            <TranslatableText text={task.title} />
                          </p>
                          <p className="text-slate-400 text-sm mt-1">
                            {formatDate(task.deadline)}
                          </p>
                        </div>
                        <span className={`text-xs font-mono px-2 py-1 rounded-sm ${
                          isOverdue ? 'bg-red-500/20 text-red-400' : 
                          daysUntil <= 1 ? 'bg-amber-500/20 text-amber-400' :
                          'bg-slate-600 text-slate-400'
                        }`}>
                          {isOverdue ? t('dashboard.overdue').toUpperCase() : daysUntil === 0 ? 'TODAY' : daysUntil === 1 ? `1 ${t('dashboard.daysLeft').toUpperCase()}` : `${daysUntil} ${t('dashboard.daysLeft').toUpperCase()}`}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state py-8">
                <Calendar className="w-12 h-12 text-slate-600 mb-3" />
                <p className="text-slate-400">No upcoming deadlines</p>
                <p className="text-slate-500 text-sm mt-1">Set deadlines on work orders to see them here</p>
              </div>
            )}
          </div>

          {/* Recent Work Orders */}
          <div className="card-industrial" data-testid="recent-tasks">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-amber-500" />
                {t('dashboard.recentWorkOrders')}
              </h2>
              <Link 
                to="/tasks" 
                className="text-amber-500 hover:text-amber-400 text-sm flex items-center gap-1"
                data-testid="view-all-tasks-link"
              >
                {t('common.viewAll')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            {recentTasks.length > 0 ? (
              <div className="space-y-3">
                {recentTasks.map((task) => (
                  <Link
                    key={task.id}
                    to={`/tasks/${task.id}`}
                    className="block p-3 bg-slate-700/50 border border-slate-600 rounded-sm hover:border-amber-500/50 transition-colors"
                    data-testid={`recent-task-${task.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`priority-indicator ${task.priority}`}></span>
                          <p className="text-white font-medium truncate">
                            <TranslatableText text={task.title} />
                          </p>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {task.category_name && (
                            <span 
                              className="text-xs px-2 py-0.5 rounded-sm"
                              style={{ backgroundColor: `${task.category_color}20`, color: task.category_color }}
                            >
                              {task.category_name}
                            </span>
                          )}
                          {task.assigned_to_name && (
                            <span className="text-slate-400 text-xs flex items-center gap-1">
                              <UserCheck className="w-3 h-3" />
                              {task.assigned_to_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className={`status-badge ${getStatusColor(task.status)}`}>
                        {getStatusLabel(task.status)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="empty-state py-8">
                <ClipboardList className="w-12 h-12 text-slate-600 mb-3" />
                <p className="text-slate-400 mb-4">{t('dashboard.noWorkOrdersYet')}</p>
                <Button 
                  onClick={() => navigate('/tasks/new')} 
                  className="btn-secondary"
                  data-testid="create-first-task-btn"
                >
                  {t('dashboard.createFirstWorkOrder')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
